
import React from 'react';
import Layout from '@/components/layout/Layout';
import { 
  BarChart3, 
  Users, 
  Music, 
  Heart,
  TrendingUp,
  Calendar
} from 'lucide-react';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useUserStats } from '@/hooks/useUserStats';
import { StatisticData, InsightItem } from '@/types/dashboard';
import DashboardHero from '@/components/dashboard/DashboardHero';
import PerformanceMetrics from '@/components/dashboard/PerformanceMetrics';
import FeaturedInsight from '@/components/dashboard/FeaturedInsight';
import QuickAccessFeatures from '@/components/dashboard/QuickAccessFeatures';
import RecommendationSection from '@/components/dashboard/RecommendationSection';
import NewsFeedSection from '@/components/dashboard/NewsFeedSection';
import FloatingActionButton from '@/components/dashboard/FloatingActionButton';
import FeatureLinks from '@/components/dashboard/FeatureLinks';
import PerformanceOverview from '@/components/dashboard/PerformanceOverview';
import RecentActivity from '@/components/dashboard/RecentActivity';

const Dashboard: React.FC = () => {
  // Get real user profile from database
  const { profile: userProfile, isLoading } = useUserProfile();
  const { data: userStats, isLoading: statsLoading } = useUserStats();
  
  // Streak information
  const streakCount = userProfile?.dailyStreak?.currentStreak || 0;
  const hasStreakToday = userProfile?.dailyStreak?.lastLoginDate ? 
    new Date(userProfile.dailyStreak.lastLoginDate).toDateString() === new Date().toDateString() : 
    false;

  // Stats data - now from real database
  const stats: StatisticData[] = [
    { 
      title: "Monthly Streams", 
      value: userStats?.monthlyStreams.toLocaleString() || "0", 
      change: `${userStats?.monthlyStreamsChange >= 0 ? '+' : ''}${userStats?.monthlyStreamsChange.toFixed(1)}%`, 
      positive: (userStats?.monthlyStreamsChange || 0) >= 0, 
      icon: BarChart3, 
      color: "neon-blue"
    },
    { 
      title: "Followers", 
      value: userStats?.followers.toLocaleString() || "0", 
      change: `${userStats?.followersChange >= 0 ? '+' : ''}${userStats?.followersChange.toFixed(1)}%`, 
      positive: (userStats?.followersChange || 0) >= 0, 
      icon: Users, 
      color: "neon-purple"
    },
    { 
      title: "Released Tracks", 
      value: userStats?.releasedTracks.toString() || "0", 
      change: `${userStats?.releasedTracksChange >= 0 ? '+' : ''}${userStats?.releasedTracksChange}`, 
      positive: (userStats?.releasedTracksChange || 0) >= 0, 
      icon: Music, 
      color: "neon-pink"
    },
    { 
      title: "Health Score", 
      value: `${userStats?.healthScore || 50}/100`, 
      change: `${userStats?.healthScoreChange >= 0 ? '+' : ''}${userStats?.healthScoreChange}pts`, 
      positive: (userStats?.healthScoreChange || 0) >= 0, 
      icon: Heart, 
      color: "neon-green"
    }
  ];
  
  // Insights data
  const insights: InsightItem[] = [
    {
      title: "Your music is gaining traction",
      description: "Your latest release 'Midnight Dreams' has seen a 24% increase in streams this week.",
      icon: TrendingUp,
      color: "from-neon-blue to-neon-purple"
    },
    {
      title: "Time to engage with your fans",
      description: "It's been 5 days since your last social post. Keep the momentum going!",
      icon: TrendingUp,
      color: "from-neon-pink to-neon-purple"
    }
  ];

  // Upcoming events
  const upcomingEvents = [
    {
      title: "Studio Session",
      date: "Today, 3:00 PM",
      type: "session"
    },
    {
      title: "Release Deadline",
      date: "Mar 25, 11:59 PM",
      type: "deadline"
    },
    {
      title: "Marketing Call",
      date: "Mar 27, 10:00 AM",
      type: "meeting"
    }
  ];

  if (isLoading || statsLoading) {
    return (
      <Layout>
        <div className="container py-6 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-64 bg-muted rounded-lg"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!userProfile) {
    return (
      <Layout>
        <div className="container py-6 max-w-7xl mx-auto">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Profile not found. Please log in.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
        {/* Hero section with personalized dashboard header */}
        <DashboardHero 
          userProfile={userProfile} 
          streakCount={streakCount} 
          hasStreakToday={hasStreakToday} 
        />
        
        {/* Artist Performance Metrics */}
        <PerformanceMetrics stats={stats} />
        
        {/* Features & Tools section */}
        <FeatureLinks />
        
        {/* Main Dashboard Content - 3-column layout with balanced content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Quick Access */}
          <div className="space-y-6">
            {/* Quick Access Feature Blocks */}
            <QuickAccessFeatures 
              userProfile={userProfile} 
              streakCount={streakCount} 
              hasStreakToday={hasStreakToday} 
            />
          </div>
          
          {/* Center Column - Performance & Insights */}
          <div className="space-y-6 md:col-span-2">
            {/* Performance Overview Chart */}
            <PerformanceOverview />
            
            {/* AI-Driven Insights */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-neon-purple" />
                AI-Powered Insights
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {insights.map((insight, index) => (
                  <FeaturedInsight
                    key={index}
                    title={insight.title}
                    description={insight.description}
                    icon={insight.icon}
                    gradientColors={insight.color}
                  />
                ))}
              </div>
            </section>
            
            {/* Side-by-side Recommendations & News */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* "We Recommend" Section */}
              <RecommendationSection />
              
              {/* News Feed */}
              <NewsFeedSection />
            </div>
          </div>
        </div>
      </div>
      
      {/* Floating Action Button */}
      <FloatingActionButton />
    </Layout>
  );
};

export default Dashboard;
